import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Sparkles, RefreshCw, MapPin, TrendingDown, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * AI-derived "Available Deals" panel for the Negotiation Room.
 *
 * Reads all chat messages in the current (product, scope, date) room and asks
 * an LLM to extract structured deal offers. Results are cached server-side and
 * re-extracted on new messages.
 *
 * Clicking a deal scrolls the chat to the source message using data-testid
 * `room-msg-${source_message_id}`.
 */
function fmtAmount(v) {
  if (v == null) return "—";
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000)   return `₹${(v / 100000).toFixed(2)} L`;
  if (v >= 1000)     return `₹${(v / 1000).toFixed(1)}k`;
  return `₹${v}`;
}

export default function RoomDealsBox({ productId, city, date }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [extractedAt, setExtractedAt] = useState(null);

  const params = useMemo(() => {
    const p = {};
    if (city) p.city = city;
    if (date && date !== "all") p.date = date;
    return p;
  }, [city, date]);

  const fetchDeals = useCallback(async (force) => {
    if (!productId) return;
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const r = await api.get(`/rooms/${productId}/deals`, {
        params: { ...params, ...(force ? { refresh: true } : {}) },
      });
      setDeals(Array.isArray(r.data.deals) ? r.data.deals : []);
      setExtractedAt(r.data.extracted_at || null);
    } catch (e) {
      if (force) toast.error("Couldn't refresh deals — try again in a moment.");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [productId, params]);

  useEffect(() => { fetchDeals(false); }, [fetchDeals]);

  const scrollToMessage = (messageId) => {
    if (!messageId) return;
    const el = document.querySelector(`[data-testid="room-msg-${messageId}"]`);
    if (!el) {
      toast.info("Source message is outside the current view.");
      return;
    }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // brief highlight
    el.style.transition = "background-color .3s ease";
    const prev = el.style.backgroundColor;
    el.style.backgroundColor = "rgba(255, 198, 0, 0.35)";
    setTimeout(() => { el.style.backgroundColor = prev; }, 1400);
  };

  return (
    <aside
      className="bhega-card p-5 order-3"
      data-testid="room-deals-box"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-full bg-[#FFBE06]/15 border border-[#FFBE06]/40 text-brand flex items-center justify-center">
            <Sparkles size={13} />
          </span>
          <h3 className="font-extrabold text-base text-foreground leading-tight">Available deals</h3>
        </div>
        <button
          type="button"
          onClick={() => fetchDeals(true)}
          disabled={refreshing}
          className="p-1.5 rounded-full border border-border text-muted-foreground hover:text-brand hover:border-[#FFBE06] disabled:opacity-40 transition-all"
          title="Refresh with AI"
          data-testid="room-deals-refresh"
          aria-label="Refresh deals with AI"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
        AI lists the price quotes buyers have shared in this chat. These are <strong className="text-foreground">buyer-reported quotes, not confirmed offers</strong> — tap one to see the source message.
      </p>

      {loading ? (
        <div className="text-xs text-muted-foreground py-6 text-center italic">Reading the chat…</div>
      ) : deals.length === 0 ? (
        <div className="text-xs text-muted-foreground py-4 text-center border border-dashed border-border">
          <TrendingDown size={16} className="inline-block mb-1 text-muted-foreground/60" />
          <div>No deals extracted yet.</div>
          <div className="text-xs mt-1">Once buyers share prices or dealer names, they&apos;ll appear here.</div>
        </div>
      ) : (
        <ul className="space-y-2 max-h-[520px] overflow-y-auto" data-testid="room-deals-list">
          {deals.map((d, idx) => (
            <li key={`${d.source_message_id}-${idx}`}>
              <button
                type="button"
                onClick={() => scrollToMessage(d.source_message_id)}
                className="w-full text-left p-3 border border-border rounded-none hover:border-[#FFBE06] hover:bg-[#FFBE06]/5 transition-all group"
                data-testid={`room-deal-${idx}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-extrabold text-lg num" data-testid={`room-deal-amount-${idx}`}>
                    {fmtAmount(d.amount)}
                    <span className="ml-2 align-middle bhega-pill pill-glow !text-xs !px-1.5 !py-0.5">Quoted</span>
                  </span>
                  <ChevronRight size={14} className="text-muted-foreground group-hover:text-brand shrink-0" />
                </div>
                {(d.dealer_name || d.dealer_location) && (
                  <div className="mt-1 text-sm text-foreground truncate">
                    {d.dealer_name || "Dealer not named"}
                    {d.dealer_location && (
                      <span className="text-muted-foreground text-xs ml-1.5 inline-flex items-center gap-0.5">
                        <MapPin size={10} /> {d.dealer_location}
                      </span>
                    )}
                  </div>
                )}
                {d.text_snippet && (
                  <blockquote
                    className="mt-2 text-xs leading-snug text-muted-foreground italic border-l-2 border-[#FFBE06]/40 pl-2 line-clamp-3"
                    data-testid={`room-deal-snippet-${idx}`}
                  >
                    &ldquo;{d.text_snippet}&rdquo;
                  </blockquote>
                )}
                <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                  <span className="truncate">Shared by <strong className="text-foreground/80">{d.alias || "Buyer"}</strong></span>
                  {d.confidence != null && (
                    <span title={`AI confidence ${Math.round((d.confidence || 0) * 100)}%`}>
                      {Math.round((d.confidence || 0) * 100)}%
                    </span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {extractedAt && (
        <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground/70 text-center">
          Powered by AI · updated {new Date(extractedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </div>
      )}
    </aside>
  );
}
